import React, { useEffect, useState, useCallback } from "react";
import { Dropdown, Form, Spinner } from "react-bootstrap";
import { ChevronDown } from "lucide-react";
import { apiCallGeneric } from "../../services/api-service";
import { sanitizeInput } from "./security-utils";
import fill_chevron_down from "../../assets/png/fill_chevron_down.png";
import close from "../../assets/png/close_filter.png";
import "./common.css";

/**
 * CommonDropdown Component
 * A reusable single-select dropdown component with asynchronous fetching and VAPT hardening.
 */
const CommonDropdown = ({
  apiUrl,
  placeholder = "Select",
  onSelect,
  value,
  searchView = false,
}) => {
  const [items, setItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);

  // Fetch from API using centralized service
  const fetchData = useCallback(async (search = "") => {
    if (!apiUrl) return;

    setLoading(true);
    try {
      const response = await apiCallGeneric("GET", apiUrl, null, {
        params: { term: search || searchTerm },
      });

      if (response && response.status === 200 && response.data.success) {
        setItems(response.data.suggestionlist || []);
      } else {
        setItems([]);
      }
    } catch (err) {
      // Error handled by api-service/interceptor internally for VAPT
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [apiUrl, searchTerm]);

  // Fetch on mount and whenever the API URL (e.g. a parent dropdown selection) changes
  useEffect(() => {
    fetchData("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl]);

  // Sync selected value from parent
  useEffect(() => {
    setSelected(value);
  }, [value]);

  // Handle search with debouncing logic
  useEffect(() => {
    if (searchTerm === "") return;

    const timeoutId = setTimeout(() => {
      fetchData(searchTerm);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, fetchData]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSelect = (item) => {
    setSelected(item);
    if (onSelect) onSelect(item);
  };

  return (
    <Dropdown style={{ width: "100%" }}>
      <Dropdown.Toggle
        variant="success"
        id="dropdown-basic"
        className={`${searchView === false
            ? "bg-transparent text-black"
            : "bg-white text-purple assigned-dropdown rounded-4 px-4"
          } d-flex align-items-center justify-content-between dp-toggle`}
        style={{ width: "100%", height: "32px", fontSize: "13px", border: "1px solid #EBEBEB" }}
      >
        <span
          style={{
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            overflow: "hidden",
            color: selected ? "#212129" : "#8790A1",
            fontWeight: "500",
          }}
        >
          {selected ? sanitizeInput(String(selected.label || "")) : placeholder}
        </span>
        <span className="d-flex align-items-center gap-2">
          {searchView === false ? (
            <ChevronDown size={18} color="#212129" />
          ) : (
            <img src={fill_chevron_down} alt="chev down" style={{ width: "12px" }} />
          )}
          {searchView === true && selected && (
            <img
              onClick={(e) => {
                e.stopPropagation();
                handleSelect(null);
              }}
              src={close}
              alt="clear"
              style={{ width: "14px", cursor: "pointer" }}
            />
          )}
        </span>
      </Dropdown.Toggle>

      <Dropdown.Menu
        className="p-2 w-100 shadow-sm border-0"
        style={{ maxHeight: "250px", overflowY: "auto", borderRadius: "8px", marginTop: "4px" }}
      >
        {/* Search box */}
        <div className="px-1 mb-2">
          <Form.Control
            type="text"
            autoComplete="off"
            placeholder="Search..."
            value={searchTerm}
            onChange={handleSearchChange}
            className="fs-13 fw-500"
            style={{ borderRadius: "6px", border: "1px solid #EBEBEB" }}
          />
        </div>

        {/* Loading spinner */}
        {loading && (
          <div className="px-2 py-1 text-muted fs-13 d-flex align-items-center gap-2">
            <Spinner animation="border" size="sm" variant="secondary" /> Loading...
          </div>
        )}

        {/* Dropdown items */}
        {!loading && items.length > 0 ? (
          items.map((item) => (
            <Dropdown.Item
              key={item.value}
              onClick={() => handleSelect(item)}
              className="fs-13 fw-500 text-dark rounded-2 my-1"
              style={{ padding: "8px 12px" }}
            >
              {sanitizeInput(String(item.label || ""))}
            </Dropdown.Item>
          ))
        ) : !loading ? (
          <div className="px-2 py-1 text-muted fs-13">No results found</div>
        ) : null}
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default CommonDropdown;
